import React from 'react';
import I18n from '@/modules/i18n';
import { Button, UnstyledButton } from '@/components/ui/Buttons';
import Popover from '@/components/ui/Popover';
import Text from '@/components/ui/Text';
import cn from '@/utils/cn';
import { RosterStudent } from './useStudentsRoster';

interface StudentRowProps {
  inactive?: boolean;
  onRemove?: (student: RosterStudent) => void;
  showRemove?: boolean;
  student: RosterStudent;
}

// AIDEV-NOTE: PLAT-16888 — `tw-group` on the row pairs with `group-hover:tw-opacity-100` /
// `group-focus-within:tw-opacity-100` on the remove button. Opacity (not `tw-invisible` /
// `visibility: hidden`) is intentional: `visibility: hidden` removes the button from the tab
// order, so keyboard users could never reach it. Opacity 0 keeps it focusable, and the
// group-focus-within rule reveals it the moment tab focus lands inside the row.
// PLAT-16784 — Clicking the X opens an inline Popover anchored to its left ("Remove? Yes")
// rather than a full modal. Confirming fires `onRemove(student)`; the parent issues the DELETE
// and adds the id to its locally-removed set, which unmounts this row and closes the popover
// implicitly. Dismissal (Esc / click-outside) just closes the popover with no side effects.
const StudentRow = ({ inactive = false, onRemove, showRemove = false, student }: StudentRowProps) => (
  <li
    className={cn(
      'tw-group tw-flex tw-items-center tw-gap-2 tw-px-2 tw-py-1.5 tw-rounded',
      'hover:tw-bg-light-grey tw-transition-colors tw-cursor-pointer',
    )}
  >
    <span
      aria-hidden="true"
      className={cn(
        'tw-flex tw-items-center tw-justify-center',
        'tw-h-6 tw-w-6 tw-rounded-full tw-text-[9px] tw-font-semibold tw-shrink-0',
        inactive ? 'tw-bg-[#EEEEEE] tw-text-tci-black' : 'tw-bg-tci-purple tw-text-white',
      )}
    >
      {student.initials}
    </span>
    <Text
      className={cn('tw-flex-1 tw-text-base tw-truncate', inactive ? 'tw-text-[#777770]' : 'tw-text-gray-900')}
      size="sm"
      spacing="sm"
      weight="semibold"
    >
      {student.full_name}
    </Text>
    {showRemove && (
      <Popover>
        <Popover.Trigger asChild>
          <UnstyledButton
            aria-label={I18n.t('students_panel_remove_student', { name: student.full_name })}
            className={cn(
              'tw-opacity-0 group-hover:tw-opacity-100 group-focus-within:tw-opacity-100',
              'data-[state=open]:tw-opacity-100',
              'tw-h-7 tw-w-7 tw-flex tw-items-center tw-justify-center tw-rounded',
              'tw-text-gray-600 hover:tw-text-gray-900 hover:tw-bg-gray-100',
              'focus-visible:tw-outline focus-visible:tw-outline-2 focus-visible:tw-outline-tci-purple',
            )}
            type="button"
          >
            <i aria-hidden="true" className="fa fa-times" />
          </UnstyledButton>
        </Popover.Trigger>
        <Popover.Content
          align="center"
          className="tw-flex tw-items-center tw-gap-2 tw-px-2 tw-py-1.5"
          side="left"
          width="unset"
        >
          <Text className="tw-m-0 tw-text-tci-black" size="sm" weight="semibold">
            {I18n.t('remove_student_popover_prompt')}
          </Text>
          <Popover.Close asChild>
            <Button small color="red" onClick={() => onRemove?.(student)}>
              {I18n.t('remove_student_popover_confirm')}
            </Button>
          </Popover.Close>
        </Popover.Content>
      </Popover>
    )}
  </li>
);

export default StudentRow;
